import React, {useEffect, useState} from "react";

import {AppContext} from "../../HomePage";
import {DashboardService} from "../../service/DashboardService";
import {CampaignContext} from "../../App";
import {isCampaignManufacturer} from "../../utils/Utils";
import './dashboard-ux.css';

// The unauthorized-sellers endpoint returns one row PER PRODUCT, each carrying a
// product_app_url and a nested competitor_unauthorized_states array (the sellers).
// Two rankings are derived from it: products by how many unauthorized sellers they
// carry, and sellers by how many distinct products they appear on.
const unauthorizedProductRows = (rows) => (rows || [])
    .map(row => {
        const hash = (row.product_app_url || '').split('#')[1];
        return {
            name: row.name,
            total: (row.competitor_unauthorized_states || []).length,
            route: hash ? '#' + hash : '#/product?unauthorized=true',
        };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

const unauthorizedSellerRows = (rows) => {
    const counts = {};
    (rows || []).forEach(row => {
        const seen = new Set();
        (row.competitor_unauthorized_states || []).forEach(s => {
            const name = (s.name || '').trim();
            if (name && !seen.has(name)) { seen.add(name); counts[name] = (counts[name] || 0) + 1; }
        });
    });
    return Object.keys(counts)
        .map(name => ({name, total: counts[name]}))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
};

// Medal icons for the top three ranks.
const MEDAL = {1: '🥇', 2: '🥈', 3: '🥉'};

const OverviewCompetitor = (props) =>{

    const {t, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);

    const [loading, setLoading] = useState(false);
    const [loadingTop10SellersViolatingMRP, setLoadingTop10SellersViolatingMRP] = useState(false);
    const [competitors, setCompetitors] = useState([]);
    const [cheapestCompetitors, setCheapestCompetitors] = useState([]);
    const [top10CheaperSellers, setTop10CheaperSellers] = useState([]);
    const [top10SellersViolatingMRP, setTop10SellersViolatingMRP] = useState([]);
    const dashboardService = new DashboardService();

    useEffect(() => {
        setLoading(true);
        dashboardService.getTop10CheaperSellers(selectedCampaign.id).then(data => {
            setTop10CheaperSellers(data);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoading(false);
        });

        if (isCampaignManufacturer(selectedCampaign)) {
            setLoadingTop10SellersViolatingMRP(true);
            dashboardService.getTopSellersViolatingMRP(selectedCampaign.id).then(data => {
                setTop10SellersViolatingMRP(data);
                setLoadingTop10SellersViolatingMRP(false);
            }).catch(error => {
                setError(error);
                setError(null);
                setLoadingTop10SellersViolatingMRP(false);
            });
        }
    }, [selectedCampaign]);

    // Unauthorized data is fetched once by the parent (Overview) and shared. Two
    // rankings: products (by # unauthorized sellers on them, linking to the product)
    // and sellers (by # products they're unauthorized on, linking to the seller report).
    const unauthorizedProducts = unauthorizedProductRows(props.unauthorizedData || []);
    const unauthorizedSellers = unauthorizedSellerRows(props.unauthorizedData || []);

    useEffect(() => {
        setCheapestCompetitors(props.overview.cheapest_competitors || []);
        setCompetitors(props.overview.competitors || []);
    }, [props.overview]);

    const rankRow = (item, index, route) => {
        const rank = index + 1;
        const name = item.name || '';
        const isOwn = item.isOwn;
        return (
            <button type="button" className={'dux-rank-row' + (isOwn ? ' dux-rank-row--own' : '')} key={name + '-' + index} onClick={() => { window.open(route, '_blank', 'noopener'); }}>
                <span className={'dux-rank-badge' + (MEDAL[rank] ? ' dux-rank-badge--medal' : '')}>{MEDAL[rank] || rank}</span>
                <span className="dux-rank-name" title={name}>{name}</span>
                {isOwn && <span className="dux-rank-you">{t('overview.you', 'Sen')}</span>}
                <span className="dux-rank-pill">{item.total}</span>
            </button>
        );
    };

    const filteredSellerRoute = (name) => '#/report/filtered_seller_price_comparison?competitorNames=' + name + ';';

    // Include my own store in the cheapest-sellers list (the backend list is
    // competitors only). total_cheapest = products where I'm the cheapest, which
    // usually tops the list.
    const ownCheapest = props.overview.total_cheapest || 0;
    const cheapestWithOwner = ownCheapest > 0
        ? [{name: selectedCampaign.name, total: ownCheapest, isOwn: true}, ...cheapestCompetitors]
            .sort((a, b) => b.total - a.total).slice(0, 10)
        : cheapestCompetitors;

    // Brand-protection panels (MAP / MRP / unauthorized): an empty list is GOOD news,
    // so render a reassuring green "all clear" state instead of a blank box, and flag
    // the alert state (red icon + count) when there are violators.
    // Brand profile shows four panels (MAP / MRP / unauthorized products / unauthorized
    // sellers); p-lg-3 fits all four on one row.
    const violationPanel = (title, icon, list, emptyText, routeOf) => {
        const alert = list.length > 0;
        const route = routeOf || ((item) => filteredSellerRoute(item.name));
        return (
            <div className="p-col-12 p-lg-3 contacts fixed_list">
                <div className={'pux-section dux-vpanel' + (alert ? ' dux-vpanel--alert' : ' dux-vpanel--ok')}>
                    <div className="dux-vpanel-head">
                        <span className="dux-vpanel-title"><i className={icon} /><span>{title}</span></span>
                        {alert && <span className="dux-vpanel-badge">{list.length}</span>}
                    </div>
                    {alert
                        ? <div className="dux-rank">
                            {list.map((item, index) => rankRow(item, index, route(item)))}
                        </div>
                        : <div className="dux-vpanel-empty">
                            <span className="dux-vpanel-empty-badge"><i className="pi pi-check-circle" /></span>
                            <span className="dux-vpanel-empty-text">{emptyText}</span>
                        </div>
                    }
                </div>
            </div>
        );
    };

    const plainPanel = (title, list, routeOf) => (
        <div className="p-col-12 p-lg-4 contacts fixed_list">
            <div className="pux-section">
                <div className="pux-section-title">{title}</div>
                <div className="dux-rank">
                    {list.map((item, index) => rankRow(item, index, routeOf(item)))}
                </div>
            </div>
        </div>
    );

    return <div className="p-grid p-fluid">
        {!props.isManufacturer &&
            plainPanel(t('overview.cheapest_competitors'), cheapestWithOwner,
                (item) => item.isOwn ? '#/product?myprice=lowest' : '#/product?cheapestCompetitor=' + item.name)}

        {/* MAP violators for a brand; cheaper-than-me competitors otherwise. */}
        {props.isManufacturer
            ? violationPanel(t('overview.resellers_violates'), 'pi pi-arrow-down', top10CheaperSellers, t('overview.no_map_violation', 'MAP ihlali yok'))
            : plainPanel(t('overview.cheaper_competitors'), top10CheaperSellers, (item) => filteredSellerRoute(item.name))}

        {props.isManufacturer &&
            violationPanel(t('overview.resellers_mrp_violates'), 'pi pi-arrow-up', top10SellersViolatingMRP, t('overview.no_mrp_violation', 'MRP ihlali yok'))}

        {/* Two unauthorized views: products (how many unauthorized sellers each carries,
            links to the product) and sellers (how many products each violates). */}
        {props.isManufacturer &&
            violationPanel(t('overview.unauthorized_products', 'Yetkisiz Satıcılı Ürünler'), 'pi pi-box', unauthorizedProducts, t('overview.no_unauthorized', 'Yetkisiz satıcı yok'), (item) => item.route)}

        {props.isManufacturer &&
            violationPanel(t('dashboard.unauthorized_sellers'), 'pi pi-user-minus', unauthorizedSellers, t('overview.no_unauthorized', 'Yetkisiz satıcı yok'))}

        {/* Brand-protection (manufacturer) profile drops the generic "top sellers by
            breadth" list: it isn't a compliance signal, includes the brand's own store,
            and duplicates the Competitor Sites table. Competitive/dropshipping keep it. */}
        {!props.isManufacturer &&
            plainPanel(t('overview.competitors'), competitors, (item) => filteredSellerRoute(item.name))}

    </div>;
};
export default OverviewCompetitor;
